/* eslint-disable no-restricted-globals */
import { action, makeObservable, observable, runInAction } from "mobx";
import { jwtDecode } from "jwt-decode";
import TmpCleaner from "./tmp/cleaner";
import TmpObject from "./tmp/object";
import TmpCategory from "./tmp/category";

const wait = (ms: number) => new Promise(
    (res, rej) => setTimeout(
        () => rej(new Error(`timed out after ${ms} ms`)),
        ms
    )
);
const generateTabId = (): string => {
    return Math.random().toString(36).substring(2, 10); // Generates a random alphanumeric string of 8 characters
};

class ApplicationUser {
    public id: number | null = null;
    public role: string | null = null;
    public token: string | null = null;
    public tabId: string | null = null;
    public rights: Array<string> | null = null;

    constructor(token: string | null) {

        let decodedToken: any = null;
        if (token) {
            try {
                decodedToken = jwtDecode(token);
            } catch (e) {
            }
        }

        this.id = decodedToken?.cleaner_id || decodedToken?.manager_id || 1 || null;
        this.role = decodedToken?.role || null;
        this.token = token;
        this.rights = decodedToken?.rights || null;
        this.tabId = decodedToken?.tabId || null;
    }

    get userToken(): string {
        return this.token || '';
    }

    public hasRight(right: string): boolean {
        if (this.rights) {
            return this.rights.includes(right);
        }
        return false;
    }

    get isManager() {
        return this.role === 'manager';
    }
}

class ApplicationSdk {
    public user: ApplicationUser | null = null;
    public tabId: string | null = null;

    public location: string = '/';
    public locationEntity: string | null = null;
    public locationAction: string | null = null;
    public currentStore: any = null;
    public currentDialog: any = null;
    public currentConfirmation: any = null;

    public toastCb: any = null;

    public cleaner: TmpCleaner | null = null;
    public objects: TmpObject;
    public categories: TmpCategory;

    constructor() {
        makeObservable(this, {
            // currentUser: observable,
            currentStore: observable,
            currentDialog: observable,
            currentConfirmation: observable,
            location: observable,
            locationEntity: observable,
            locationAction: observable,
            user: observable,
            cleaner: observable,
            objects: observable,
            categories: observable,
            routeTo: action,
            init: action,
            useStore: action,
            closeDialog: action,
            openDialog: action,
            openDeleteConfirmationDialog: action,
            closeDeleteConfirmationDialog: action
        });

        this.location = window.location?.pathname || '/';
        this.locationEntity = this.location.split('/')?.[1] || null;
        this.locationAction = this.location.split('/')?.[2] || null;

        this.cleaner = new TmpCleaner(this);
        this.objects = new TmpObject(this);
        this.categories = new TmpCategory(this);

        console.log('locationEntity', this.locationEntity);
        // @ts-ignore
        window.sdk = this;

    }

    public routeTo(path: string): void {
        history.pushState({}, "", path);
        this.location = path;
        this.locationEntity = this.location.split('/')?.[1] || null;
        this.locationAction = this.location.split('/')?.[2] || null;
    }

    public async init(): Promise<void> {
        // const path: string | undefined = window?.location?.pathname;
        const searchData: string | undefined = window?.location?.search;
        const searchParams: URLSearchParams = new URLSearchParams(searchData);

        if (process.env.REACT_APP_DEV_MODE === 'true') {
            this.tabId = localStorage.getItem('tabId') || generateTabId();
            localStorage.setItem('session-tab-id', this.tabId);
        } else {
            this.tabId = sessionStorage.getItem('tabId') || generateTabId();
            sessionStorage.setItem('session-tab-id', this.tabId);
        }

        let clientToken: string | null = null;

        if (this.locationEntity === 'signabledocuments') {
            console.log(this.locationAction)
            return;
        }

        if (this.locationEntity === 'auth' && this.locationAction === 'login') {
            // if we do not have a token return to error page
            if (!searchParams?.has('token')) {
                this.routeToError();
                return;
            }

            const fetchToken = await this.fetchApi('auth', 'login', {
                token: searchParams.get('token') || ''
            });

            clientToken = fetchToken?.json?.token || null;

            if (!clientToken) {
                this.routeToError();
                return;
            }
            if (process.env.REACT_APP_DEV_MODE === 'true') {
                localStorage.setItem('token', clientToken);
            } else {
                sessionStorage.setItem('token', clientToken);
            }
        } else if (searchParams?.has('token')) {
            // if we have a token in the url, we use this token to login - This is only for cleaner login
            clientToken = searchParams.get('token') || '';
        } else if (sessionStorage.getItem('token') || localStorage.getItem('token')) {
            clientToken = localStorage.getItem('token') || sessionStorage.getItem('token') || null;
        } else {
            this.routeToError();
            return;
        }

        runInAction(() => {
            this.user = new ApplicationUser(clientToken);
        })

        if (this.user?.isManager) {
            this.routeToManager();
        }

        // reset search Params
        window.history.replaceState({}, document.title, window.location.pathname);
    }

    public async uploadImage(entity: string, action: string, image: any, options: any = {
        timeout: 10000,
    }): Promise<any> {
        const useUrl = `${process.env.REACT_APP_BACKEND_API}${entity}/${action}`;
        const formData = new FormData();
        formData.append('file', image);

        return new Promise((resolve, reject) => {
            const abortController = new AbortController();
            const fetchData = fetch(useUrl, {
                method: 'POST',
                signal: abortController.signal,
                // @ts-ignore
                headers: {
                    Authorization: `Bearer ${this.user?.userToken || ''}`,
                    'X-Tab-Id': this.currentTabId
                },
                body: formData
            });

            const fetchOrTimeout = Promise.race([fetchData, wait(options.timeout)]);

            fetchOrTimeout
                .then(async (response: any) => {
                    // if (useSpinner) useSpinner.close();
                    let jsonResult = null;
                    try {
                        jsonResult = await response?.json()
                    } catch (e) {
                    }
                    resolve({
                        json: jsonResult || null,
                        status: response.status
                    })
                })
                .catch((error: any) => {
                    // if (useSpinner) useSpinner.close();
                    abortController.abort()
                    resolve(undefined)
                });
        })
    }

    public async fetchApi(entity: string, action: string, data: any = {}, options: any = {
        timeout: 10000,
        spinner: true,
        multiPart: false
    }): Promise<any> {
        const fetchResult = await this.fetchApiData(entity, action, data, options);

        if (fetchResult?.status === 401) {
            this.routeToError();
        }

        return fetchResult;
    }

    public routeToManager(): void {
        if (process.env.REACT_APP_DEV_MODE !== 'true') {
            localStorage.removeItem('token');
        }
        this.routeTo('/edit/orders');
    }

    public routeToError(): void {
        localStorage.removeItem('token');
        this.routeTo('/auth/error');
    }

    public async fetchApiFile(entity: string, action: string, options: any = {
        timeout: 10000,
        spinner: true,
        multiPart: false
    }): Promise<any> {
        const useUrl = `${process.env.REACT_APP_BACKEND_API}${entity}/${action}`;

        return new Promise((resolve, reject) => {
            const abortController = new AbortController();
            const fetchData = fetch(useUrl, {
                method: 'GET',
                signal: abortController.signal
            });

            const fetchOrTimeout = Promise.race([fetchData, wait(options.timeout)]);

            fetchOrTimeout
                .then(async (response: any) => {
                    // if (useSpinner) useSpinner.close();
                    let blob = null;
                    console.log('fetchApiFile', response);
                    try {
                        blob = await response.blob();
                    } catch (e) {
                    }
                    resolve({
                        blob,
                        status: response.status
                    })
                })
                .catch((error: any) => {
                    console.log('fetchApiFile error', error);
                    // if (useSpinner) useSpinner.close();
                    abortController.abort()
                    resolve(undefined)
                });
        })
    }
    public async fetchApiData(entity: string, action: string, data: any = {}, options: any = {
        timeout: 10000,
        spinner: true,
        multiPart: false
    }): Promise<any> {
        const useUrl = `${process.env.REACT_APP_BACKEND_API}${entity}/${action}`;

        return new Promise((resolve, reject) => {
            const abortController = new AbortController();
            const fetchData = fetch(useUrl, {
                method: 'POST',
                signal: abortController.signal,
                // @ts-ignore
                headers: {
                    'Content-Type': options?.multiPart ? 'multipart/form-data' : 'application/json',
                    Authorization: `Bearer ${this.user?.userToken || ''}`,
                    'X-Tab-Id': this.currentTabId
                },
                body: JSON.stringify(data || {})
            });

            const fetchOrTimeout = Promise.race([fetchData, wait(options.timeout)]);

            fetchOrTimeout
                .then(async (response: any) => {
                    // if (useSpinner) useSpinner.close();
                    let jsonResult = null;
                    try {
                        jsonResult = await response?.json()
                    } catch (e) {
                    }
                    resolve({
                        json: jsonResult || null,
                        status: response.status
                    })
                })
                .catch((error: any) => {
                    // if (useSpinner) useSpinner.close();
                    abortController.abort()
                    resolve(undefined)
                });
        })

    }

    public useStore(store: any): void {
        if (this.currentStore?.onLeaveStore) {
            this.currentStore.onLeaveStore();
        }

        this.currentStore = new store(this);
        this.currentStore.init();
    }

    public openDeleteConfirmationDialog(title: string, text: string, callback: any, searchWord: string = 'löschen'): void {
        this.currentConfirmation = {
            title,
            text,
            searchWord,
            callback
        }
    }

    public closeDeleteConfirmationDialog(): void {
        this.currentConfirmation = null;
    }

    public openDialog(store: any, config: any = {}): void {
        this.currentDialog = new store(this);
        this.currentDialog.init(config);
    }

    public closeDialog(event: any = null): void {
        const dialogOnLeave: string = this.currentDialog.id;
        this.currentDialog = null;
        if (this.currentStore?.onDialogLeave) {
            this.currentStore.onDialogLeave(dialogOnLeave, event);
        }
    }

    public showSuccessToast(message: string): void {
        if (this.toastCb) {
            this.toastCb({
                severity: 'success',
                summary: 'Erfolgreich',
                life: 3000,
                message
            });
        }
    }

    public showErrorToast(message: string): void {
        if (this.toastCb) {
            this.toastCb({
                severity: 'error',
                summary: 'Fehler',
                life: 3000,
                message
            });
        }
    }

    public registerToastCallback(cb: any): void {
        this.toastCb = cb;
    }

    get currentTabId(): string {
        return this.user?.tabId || this.tabId || '';
    }
}

let AppSdk: ApplicationSdk | undefined = undefined;

const getSdk = () => {
    if (!AppSdk) {
        AppSdk = new ApplicationSdk();
        AppSdk.init();
    }

    return AppSdk;
}

export default getSdk;
export {
    ApplicationSdk
}